'use client';

import Link from '@eliadmin/core/src/components/link';
import React from 'react';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { Carousel } from '../ui/carousel';
import {
  type BuildProductQueryOptions,
  buildProductsQuery,
} from '@eliadmin/core/src/utils/product-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { ProductsSkeleton } from '../product/products-skeleton';
import { CategoryTab } from '../ui/category-tab';
import { CarouselWall } from '@eliadmin/core/src/components/ui/carousel-wall';

interface HomepageProductsCarouselProps {
  categories: CategoriesEntity[];
  queries: BuildProductQueryOptions;
  title: string;
  className?: {
    section?: string;
  };
}

export function HomepageProductsCarousel({
  categories,
  className,
  title,
  queries,
}: HomepageProductsCarouselProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const isInitialized = useCustomerStore(s => s.isInitialized);

  const [activeCategoryId, setActiveCategoryId] = React.useState<number>(
    Number(categories[0]?.id ?? 0),
  );

  const activeCategory = categories.find(c => c.id === activeCategoryId);

  const { t } = useTranslations();

  const options = {
    align: 'start' as const,
  };

  const productsQueries = buildProductsQuery({
    ...queries,
    showAttributes: true,
    categories: [activeCategoryId],
  });

  const productsQuery = useInfiniteQuery({
    queryKey: [CacheKey.PRODUCTS, 'homepage-infinite', locale, authToken ?? null, productsQueries],
    queryFn: async ({ pageParam }) => {
      return await fetchProducts({
        locale,
        authToken,
        query: {
          ...productsQueries,
          page: pageParam,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: Number(productsQueries.page ?? 1),
    enabled: isInitialized,
  });

  const products = productsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
  const isLoading = productsQuery.isLoading;

  return (
    <section className={cn('custom-section', 'gap-6 pr-0 sm:pr-4', className?.section)}>
      <Heading
        className={{ container: cn('custom-container', 'pr-4 sm:pr-0') }}
        title={title}
        tag="h2"
      />
      <div
        className={cn('custom-container', 'flex w-full items-center justify-between pr-4 sm:pr-0')}
      >
        <div className="grid w-full grid-cols-3 items-center justify-start bg-[#EDEFF2] p-1 sm:flex sm:w-fit ">
          {categories.map(category => (
            <CategoryTab
              key={category.slug}
              category={category}
              activeCategoryId={activeCategoryId}
              setActiveCategoryId={setActiveCategoryId}
            />
          ))}
        </div>
        <Button asChild variant={'dark'} className="hidden whitespace-nowrap sm:flex">
          <Link prefetch={false} href={ROUTES.CATEGORY({ slug: activeCategory?.slug || '' })}>
            {t('text3439')}
          </Link>
        </Button>
      </div>
      {isLoading ? (
        <ProductsSkeleton amount={4} />
      ) : (
        <>
          <div className={cn('custom-container', 'relative overflow-visible')}>
            <CarouselWall />
            <Carousel
              options={options}
              className={{
                main: 'w-full overflow-visible',
                container: 'w-full gap-4 overflow-visible sm:gap-5 lg:gap-6',
                wrapper: 'w-full overflow-visible',
                slide: 'w-full max-w-[282px]',
              }}
              slides={products.map(product => (
                <ProductCard product={product} key={product.id} imageSizes="282px" />
              ))}
            />
          </div>
          <div className="block w-full pr-4 sm:hidden">
            <Button asChild variant={'dark'} className="w-full">
              <Link prefetch={false} href={ROUTES.CATEGORY({ slug: activeCategory?.slug || '' })}>
                {t('text3439')}
              </Link>
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
